import { type DragEvent, useId, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { enumerer, estEntiere, notesReportees, pourcent, toutEntoure } from "./cadres-textes";
import { CARTE, EnTete, FOCUS, LongOuCourt, PETIT_BOUTON, PETIT_BOUTON_ETEINT } from "./cadres-ui";
import type { ApercuReponse, PlaqueApercu } from "./contract";

/**
 * La liste d'ordre des plaques du module Cadres (#167, tache B4 ; maquettes `Cadres.dc.html`
 * et `CadresEtats.dc.html`). Une ligne par couleur, de l'avant vers le fond : poignee,
 * pastille, nom, part de la fenetre, plaques qu'elle donne, et deux boutons pour la monter ou
 * la descendre. On la glisse a la souris par sa poignee ; au clavier et au telephone, ce sont
 * les boutons (un glisser ne se fait pas au clavier).
 *
 * **L'ordre montre est celui des plaques obtenues** (`apercu.ordre`), sauf pendant qu'un ordre
 * demande attend son calcul : la liste montre alors tout de suite l'ordre demande, et grise les
 * plaques, qui sont encore celles de l'apercu affiche.
 *
 * **Le focus suit la couleur deplacee.** Les lignes sont cles par leur rang, pas par leur
 * couleur : React ne deplace donc aucun noeud (un bouton deplace dans le DOM perdrait le focus),
 * et c'est la carte qui rend le focus au bouton de la couleur, a son nouveau rang. Un bouton
 * eteint aux bouts reste focalisable (`aria-disabled`), pour la meme raison.
 */

/** L'ordre avec la couleur de rang `de` placee au rang `vers` ; tel quel hors des bornes. */
export function deplacer(ordre: readonly string[], de: number, vers: number): string[] {
  const copie = [...ordre];
  const dedans = (rang: number): boolean => rang >= 0 && rang < copie.length;
  if (!dedans(de) || !dedans(vers) || de === vers) return copie;
  const [couleur] = copie.splice(de, 1);
  if (couleur !== undefined) copie.splice(vers, 0, couleur);
  return copie;
}

/**
 * Les plaques d'une couleur : « plaque 2 », « plaques 6 et 8 ». La maquette ne dit pas « au
 * fond » ici : la note sous la ligne dit deja ce qui passe au fond, et pourquoi.
 */
export function libellePlaques(couleur: string, plaques: readonly PlaqueApercu[]): string {
  const rangs = plaques.filter((plaque) => plaque.couleur === couleur).map((plaque) => plaque.rang);
  if (rangs.length === 0) return "aucune plaque";
  if (rangs.length === 1) return `plaque ${rangs[0]}`;
  return `plaques ${enumerer(rangs)}`;
}

export type Compteur = { plaques: string; suite: string };

/**
 * Le compteur sous la liste : « 7 plaques · l'ordre propose », « 8 plaques · une de plus que
 * l'ordre propose ». La maquette met le nombre en gras et la suite apres un point median.
 */
export function compteur(n: number, nPropose: number, estLOrdrePropose: boolean): Compteur {
  const plaques = `${n} plaque${n > 1 ? "s" : ""}`;
  if (estLOrdrePropose) return { plaques, suite: "l'ordre proposé" };
  const ecart = n - nPropose;
  if (ecart === 0) return { plaques, suite: "autant que l'ordre proposé" };
  const combien = Math.abs(ecart) === 1 ? "une" : String(Math.abs(ecart));
  return { plaques, suite: `${combien} de ${ecart > 0 ? "plus" : "moins"} que l'ordre proposé` };
}

/** Deux ordres de requete egaux : tous deux nuls (l'ordre propose), ou les memes couleurs. */
function memeDemande(a: readonly string[] | null, b: readonly string[] | null): boolean {
  if (a === null || b === null) return a === b;
  return a.length === b.length && a.every((couleur, rang) => couleur === b[rang]);
}

type Sens = "haut" | "bas";

/** Les fleches de la maquette : caracteres de texte, lus par le nom du bouton. */
const FLECHE: Record<Sens, string> = { haut: "↑", bas: "↓" };

export function CarteOrdre({
  apercu,
  demande = null,
  choisi,
  onOrdre,
  className,
}: {
  apercu: ApercuReponse;
  /** L'ordre de la requete qui a produit cet apercu ; `null` : l'ordre propose. */
  demande?: readonly string[] | null;
  /**
   * L'ordre que la page a choisi, envoye ou en attente de l'etre (`null` : l'ordre propose).
   * Tant qu'il n'est pas celui de l'apercu affiche, la liste le montre tout de suite, plaques
   * grisees. Par defaut, `demande`.
   */
  choisi?: readonly string[] | null;
  /** Le nouvel ordre, ou `null` pour revenir a l'ordre propose. */
  onOrdre: (ordre: string[] | null) => void;
  className?: string;
}) {
  const id = useId();
  const choix = choisi === undefined ? demande : choisi;
  const attente = !memeDemande(choix, demande);
  const ordre = attente ? (choix ?? apercu.ordre_propose) : apercu.ordre;
  // L'ordre montre est l'ordre propose quand sa requete n'en porte aucun (`null`), et jamais
  // parce que deux tableaux se ressemblent : la partie A peut rendre un `ordre_propose` qui n'est
  // pas l'ordre effectif de ses plaques, et « Revenir a l'ordre propose » paraissait alors actif
  // sans rien faire (relecture de #176).
  const estPropose = choix === null;
  const { plaques, suite } = compteur(
    apercu.plaques.length,
    apercu.plaques_ordre_propose,
    demande === null,
  );
  const [annonce, setAnnonce] = useState("");
  const [saisie, setSaisie] = useState<number | null>(null);
  const [survolee, setSurvolee] = useState<number | null>(null);
  const liste = useRef<HTMLOListElement>(null);
  const boutons = useRef(new Map<string, HTMLButtonElement>());
  const aSuivre = useRef<{ couleur: string; sens: Sens } | null>(null);

  // Le focus suit la couleur deplacee, tant qu'il est reste dans la liste (ou qu'il s'est
  // perdu) : si Nicolas est parti ailleurs, la carte ne le lui reprend pas.
  useLayoutEffect(() => {
    const cible = aSuivre.current;
    if (!cible) return;
    const actif = document.activeElement;
    const resteIci =
      actif === null || actif === document.body || (liste.current?.contains(actif) ?? false);
    const rang = ordre.indexOf(cible.couleur);
    if (!resteIci || rang < 0) {
      aSuivre.current = null;
      return;
    }
    boutons.current.get(`${rang}-${cible.sens}`)?.focus();
  }, [ordre]);

  const deplacerVers = (de: number, vers: number, sens: Sens | null): void => {
    const couleur = ordre[de];
    if (couleur === undefined || vers < 0 || vers >= ordre.length || de === vers) return;
    aSuivre.current = sens ? { couleur, sens } : null;
    setAnnonce(`${couleur} en position ${vers + 1} sur ${ordre.length}.`);
    onOrdre(deplacer(ordre, de, vers));
  };

  const lacher = (event: DragEvent<HTMLLIElement>, vers: number): void => {
    event.preventDefault();
    const lu = Number.parseInt(event.dataTransfer.getData("text/plain"), 10);
    const de = saisie ?? (Number.isInteger(lu) ? lu : null);
    setSaisie(null);
    setSurvolee(null);
    if (de !== null) deplacerVers(de, vers, null);
  };

  // Ce que l'outil a garde plus au fond que ce que Nicolas demandait : ses zones etaient toutes
  // entourees a ce rang. Seulement quand l'apercu affiche repond a l'ordre montre. Depuis la
  // tete b311690, `reportees` compte ces couleurs, avec ce qui les entoure : cette comparaison
  // ne sert plus qu'a un products plus ancien, qui ne les comptait pas.
  const repoussees = new Set(
    !attente && demande
      ? ordre.filter((couleur, rang) => {
          const demandee = demande.indexOf(couleur);
          return demandee >= 0 && demandee < rang;
        })
      : [],
  );

  return (
    <section aria-labelledby={`${id}-titre`} className={cn(CARTE, className)}>
      <EnTete
        aparte={<LongOuCourt court="avant → fond" long="de l'avant vers le fond" />}
        id={`${id}-titre`}
        titre="Ordre des plaques"
      />
      <p className="-mt-1.5 mb-2 hidden text-[13px] text-muted-foreground md:block">
        Glisse une couleur, ou monte-la et descends-la avec les flèches.
      </p>
      <ol aria-labelledby={`${id}-titre`} className="flex flex-col" ref={liste}>
        {ordre.map((couleur, rang) => {
          const teinte = apercu.palette.find((entree) => entree.nom === couleur);
          // Deux notes au plus par couleur, puis le compte des autres : des anneaux
          // concentriques en donnaient huit sous chaque couleur (relecture de #176).
          const siennes = (attente ? [] : apercu.reportees).filter(
            (reportee) => reportee.couleur === couleur,
          );
          const dite = siennes.some((reportee) => estEntiere(reportee, apercu.plaques));
          const notes = [
            ...(repoussees.has(couleur) && !dite ? [toutEntoure()] : []),
            ...notesReportees(siennes, apercu.plaques),
          ];
          const enTete = rang === 0;
          const enQueue = rang === ordre.length - 1;
          return (
            <li
              className={cn(
                "grid grid-cols-[16px_18px_minmax(0,1fr)_auto_52px] items-center gap-x-2 gap-y-0.5 border-t border-border py-[7px] text-sm first:border-t-0",
                saisie === rang && "rounded-lg border-transparent bg-muted px-1.5 shadow-md",
                survolee === rang && saisie !== rang && "bg-muted/60",
              )}
              data-saisie={saisie === rang ? "true" : undefined}
              // biome-ignore lint/suspicious/noArrayIndexKey: une ligne par rang, pour qu'aucun noeud ne bouge et que le focus reste (voir plus haut)
              key={rang}
              onDragLeave={() => setSurvolee((actuelle) => (actuelle === rang ? null : actuelle))}
              onDragOver={(event) => {
                if (saisie === null) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setSurvolee(rang);
              }}
              onDrop={(event) => lacher(event, rang)}
            >
              <span
                aria-hidden="true"
                className="cursor-grab select-none text-[15px] leading-none text-muted-foreground"
                draggable
                onDragEnd={() => {
                  setSaisie(null);
                  setSurvolee(null);
                }}
                onDragStart={(event) => {
                  event.dataTransfer.setData("text/plain", String(rang));
                  event.dataTransfer.effectAllowed = "move";
                  const ligne = event.currentTarget.closest("li");
                  if (ligne) event.dataTransfer.setDragImage?.(ligne, 12, 12);
                  setSaisie(rang);
                }}
                title="Glisser pour déplacer"
              >
                ⠿
              </span>
              <span
                aria-hidden="true"
                className="size-[18px] rounded border border-border"
                style={{ backgroundColor: teinte?.hexa }}
              />
              <span className="min-w-0 truncate">
                {couleur}{" "}
                <span className="text-[12.5px] text-muted-foreground">
                  · {teinte ? pourcent(teinte.surface_pct) : "?"}
                </span>
              </span>
              <span
                className={cn(
                  "whitespace-nowrap text-[13px] text-foreground/80 transition-opacity",
                  attente && "opacity-50",
                )}
                data-plaques
              >
                {libellePlaques(couleur, apercu.plaques)}
              </span>
              <span className="inline-flex gap-1">
                {(["haut", "bas"] as const).map((sens) => {
                  const eteint = sens === "haut" ? enTete : enQueue;
                  return (
                    <button
                      aria-disabled={eteint || undefined}
                      aria-label={`${sens === "haut" ? "Monter" : "Descendre"} ${couleur}`}
                      className={cn(
                        "inline-flex size-6 items-center justify-center rounded-md border border-input text-[12px] aria-disabled:border-border",
                        FOCUS,
                        eteint
                          ? "cursor-default bg-card text-muted-foreground/40"
                          : "bg-card text-foreground hover:bg-muted",
                      )}
                      key={sens}
                      onClick={() => {
                        if (!eteint)
                          deplacerVers(rang, sens === "haut" ? rang - 1 : rang + 1, sens);
                      }}
                      ref={(bouton) => {
                        if (bouton) boutons.current.set(`${rang}-${sens}`, bouton);
                        else boutons.current.delete(`${rang}-${sens}`);
                      }}
                      type="button"
                    >
                      <span aria-hidden="true">{FLECHE[sens]}</span>
                    </button>
                  );
                })}
              </span>
              {notes.map((note) => (
                <p
                  className="col-start-3 col-end-6 mt-0.5 rounded-md border border-avertissement/60 bg-avertissement/15 px-2 py-1 text-[12.5px]"
                  key={note}
                >
                  {note}
                </p>
              ))}
            </li>
          );
        })}
      </ol>
      <p className="sr-only" role="status">
        {annonce}
      </p>
      {/* Sur une ligne, comme la maquette : le texte du compteur passe a la ligne, pas le bouton. */}
      <div className="mt-3 flex items-center justify-between gap-2.5 border-t border-border pt-3 text-sm">
        <p className={cn("min-w-0 transition-opacity", attente && "opacity-50")}>
          <strong className="font-semibold">{plaques}</strong>
          <span className={cn(suite === "l'ordre proposé" && "hidden md:inline")}> · {suite}</span>
        </p>
        <button
          aria-disabled={estPropose || undefined}
          className={cn(estPropose ? PETIT_BOUTON_ETEINT : PETIT_BOUTON, "shrink-0")}
          onClick={() => {
            if (!estPropose) onOrdre(null);
          }}
          type="button"
        >
          <LongOuCourt court="Ordre proposé" long="Revenir à l'ordre proposé" />
        </button>
      </div>
    </section>
  );
}
